import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Search,
  ShieldCheck,
  Sparkles,
  Menu,
  UserCircle,
  UserPen,
  LogOut,
  X,
  BellRing,
  Check,
  Monitor,
  Camera,
} from "lucide-react";
import Cropper from "react-easy-crop";
import client from "../../src/api/client";
import { fileToDataUrl, getCroppedImageDataUrl } from "../../src/utils/imageCrop";
import "../styles/admin-base.css";
import {
  applyAppearancePreferences,
  loadStoredAppearancePreferences,
  normalizeAppearancePreferences,
  saveAppearancePreferences,
} from "../lib/appearancePreferences";

const sanitizeEnvText = (value, fallback) => {
  if (value === null || value === undefined) {
    return fallback;
  }
  const normalized = value.toString().replace(/^['"]|['"]$/g, "").trim();
  return normalized.length ? normalized : fallback;
};

const computeInitials = (name) => {
  if (!name) {
    return "AU";
  }
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
};

const readStoredAdminUser = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem("adminUser");
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    console.error("Failed to parse admin user from storage", error);
    return null;
  }
};

const getAvatarStore = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return {};
  }
  try {
    const raw = window.localStorage.getItem("adminAvatars") || "{}";
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    console.error("Failed to parse admin avatars from storage", error);
    return {};
  }
};

const persistAvatarForAdmin = (adminId, dataUrl) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  const store = getAvatarStore();
  if (dataUrl) {
    store[adminId || "default"] = dataUrl;
  } else {
    delete store[adminId || "default"];
  }
  try {
    window.localStorage.setItem("adminAvatars", JSON.stringify(store));
  } catch (error) {
    console.error("Failed to persist admin avatar", error);
  }
};

const readAvatarForAdmin = (adminId) => {
  const store = getAvatarStore();
  return store[adminId || "default"] || null;
};

const buildAdminDisplayName = (user) => {
  if (!user) {
    return null;
  }
  const firstName = user.firstName ?? user?.admin?.firstName ?? "";
  const lastName = user.lastName ?? user?.admin?.lastName ?? "";
  const normalized = [firstName, lastName]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
  return normalized || user.username || user.email || null;
};

const resolveAdminId = (user) => {
  if (!user) {
    return null;
  }
  const candidateIds = [user.id, user.adminId, user?.admin?.id]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);
  return candidateIds[0] ?? null;
};

const createEmptySearchResults = () => ({
  members: [],
  tickets: [],
  registrations: [],
  events: [],
  bookings: [],
  venues: [],
  idCards: [],
});

const normalizeSearchResults = (value) => {
  const next = value && typeof value === "object" ? value : {};
  return {
    members: Array.isArray(next.members) ? next.members : [],
    tickets: Array.isArray(next.tickets) ? next.tickets : [],
    registrations: Array.isArray(next.registrations) ? next.registrations : [],
    events: Array.isArray(next.events) ? next.events : [],
    bookings: Array.isArray(next.bookings) ? next.bookings : [],
    venues: Array.isArray(next.venues) ? next.venues : [],
    idCards: Array.isArray(next.idCards) ? next.idCards : [],
  };
};

const parsePositiveNumber = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const createEmptyNotificationsState = () => ({
  badgeCount: 0,
  unreadCount: 0,
  buckets: [],
  items: [],
  generatedAt: null,
});

const normalizeNotificationsState = (value) => {
  const next = value && typeof value === "object" ? value : {};
  return {
    badgeCount: Number(next.badgeCount ?? 0),
    unreadCount: Number(next.unreadCount ?? 0),
    buckets: Array.isArray(next.buckets) ? next.buckets : [],
    items: Array.isArray(next.items) ? next.items : [],
    generatedAt: next.generatedAt ?? new Date().toISOString(),
  };
};

const parseNotificationDate = (item) => {
  const raw = item?.createdAt ?? item?.created_at;
  if (!raw) {
    return null;
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const groupNotificationsByDate = (items) => {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const grouped = {
    new: [],
    today: [],
    earlier: [],
  };

  items.forEach((item) => {
    const createdAt = parseNotificationDate(item);
    if (!createdAt) {
      grouped.earlier.push(item);
      return;
    }

    const ageMs = now.getTime() - createdAt.getTime();
    if (ageMs <= 60 * 60 * 1000) {
      grouped.new.push(item);
      return;
    }

    if (createdAt >= startOfToday) {
      grouped.today.push(item);
      return;
    }

    grouped.earlier.push(item);
  });

  return [
    { key: "new", label: "New", items: grouped.new },
    { key: "today", label: "Today", items: grouped.today },
    { key: "earlier", label: "Earlier", items: grouped.earlier },
  ].filter((section) => section.items.length);
};

const resolveNotificationInitial = (item) => {
  const source = item?.title || item?.message || "N";
  return source.trim().charAt(0).toUpperCase() || "N";
};

const TOPBAR_PAGE_META = {
  dashboard: {
    title: "Dashboard",
    subtitle: "Overview of platform activity, alerts, and key metrics.",
  },
  members: {
    title: "Member Management",
    subtitle: "Review member records, status, and lifecycle updates.",
  },
  "registration-review": {
    title: "Registration Review",
    subtitle: "Validate submissions and process onboarding approvals.",
  },
  "id-card-management": {
    title: "ID Card Management",
    subtitle: "Track card requests, generation, and release status.",
  },
  tickets: {
    title: "Requests and Tickets",
    subtitle: "Monitor support conversations and resolve requests quickly.",
  },
  "event-management": {
    title: "Event Management",
    subtitle: "Organize schedules, attendance, and event operations.",
  },
  "venue-management": {
    title: "Venue Management",
    subtitle: "Manage venue inventory, availability, and utilization.",
  },
  "client-bookings": {
    title: "Client Bookings",
    subtitle: "Handle booking requests and calendar coordination.",
  },
  "reports-analytics": {
    title: "Reports and Analytics",
    subtitle: "Analyze operational trends and administrative performance.",
  },
  "audit-log": {
    title: "Audit Log",
    subtitle: "Inspect security-sensitive actions and account events.",
  },
  "admin-settings": {
    title: "Admin Settings",
    subtitle: "Configure platform behavior, security, and preferences.",
  },
  notifications: {
    title: "Notifications",
    subtitle: "Centralized updates across members, tickets, and operations.",
  },
};

export default function TopBar({ onToggleSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();
  const defaultAdminName = sanitizeEnvText(process.env.REACT_APP_DEFAULT_ADMIN_NAME, "Admin User");
  const defaultAdminId = sanitizeEnvText(process.env.REACT_APP_DEFAULT_ADMIN_ID, "");
  const MIN_SEARCH_LENGTH = 2;
  const ALL_NOTIFICATIONS_LIMIT = 20;

  const [profile, setProfile] = useState({
    fullName: defaultAdminName,
    title: "Admin User",
    initials: computeInitials(defaultAdminName),
  });
  const [notifications, setNotifications] = useState(createEmptyNotificationsState);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState(null);
  const [security, setSecurity] = useState({
    message: "Secure channel active",
    lastEventAgo: null,
    healthy: true,
    activeSessions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchResults, setSearchResults] = useState(createEmptySearchResults);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationFilter, setNotificationFilter] = useState("all");
  const [notificationsMenuOpen, setNotificationsMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [appearancePrefs, setAppearancePrefs] = useState(loadStoredAppearancePreferences);
  const [appearanceSaving, setAppearanceSaving] = useState(false);
  const [appearanceStatus, setAppearanceStatus] = useState(null);
  const [accountForm, setAccountForm] = useState({ firstName: "", lastName: "" });
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const [accountErrors, setAccountErrors] = useState({});
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarCropSource, setAvatarCropSource] = useState(null);
  const [avatarCrop, setAvatarCrop] = useState({ x: 0, y: 0 });
  const [avatarZoom, setAvatarZoom] = useState(1);
  const [avatarCroppedAreaPixels, setAvatarCroppedAreaPixels] = useState(null);
  const [isApplyingCrop, setIsApplyingCrop] = useState(false);

  const searchContainerRef = useRef(null);
  const notificationsRef = useRef(null);
  const profileRef = useRef(null);
  const lastSearchRequestId = useRef(0);

  useEffect(() => {
    applyAppearancePreferences(appearancePrefs);
    saveAppearancePreferences(appearancePrefs);
  }, [appearancePrefs]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (settingsModalOpen) {
      setAccountForm({
        firstName: profile.firstName ?? "",
        lastName: profile.lastName ?? "",
      });
      setAccountStatus(null);
      setAccountErrors({});
      const currentUser = readStoredAdminUser();
      const resolvedId = resolveAdminId(currentUser) ?? resolveAdminId({ id: defaultAdminId }) ?? "default";
      const storedAvatar = readAvatarForAdmin(resolvedId);
      setAvatarPreview(storedAvatar || avatarUrl || null);
      setAvatarCropSource(null);
      setAvatarCrop({ x: 0, y: 0 });
      setAvatarZoom(1);
      setAvatarCroppedAreaPixels(null);
      setIsApplyingCrop(false);
    }
  }, [settingsModalOpen, profile.firstName, profile.lastName, avatarUrl, defaultAdminId]);

  useEffect(() => {
    let isMounted = true;

    // Load user from localStorage
    const storedUser = readStoredAdminUser();
    if (storedUser) {
      const displayName = buildAdminDisplayName(storedUser) ?? "Admin User";
      setProfile((prev) => ({
        ...prev,
        fullName: displayName,
        title: Array.isArray(storedUser.roles) && storedUser.roles.length ? storedUser.roles.join(", ") : "Admin",
        initials: computeInitials(displayName),
      }));
    }

    const fetchAppearance = async () => {
      try {
        const response = await client.get("/api/admin/settings/system");
        if (!isMounted) {
          return;
        }
        const serverPrefs = response?.data?.settings?.appearancePreferences;
        if (!serverPrefs) {
          return;
        }
        const normalized = normalizeAppearancePreferences(serverPrefs);
        setAppearancePrefs((prev) => (
          prev.theme === normalized.theme && prev.fontScale === normalized.fontScale
            ? prev
            : normalized
        ));
      } catch (fetchError) {
        console.error("Unable to load appearance preferences", fetchError);
      }
    };

    const fetchOverview = async () => {
      try {
        const params = {};
        const currentUser = readStoredAdminUser();
        const resolvedId = resolveAdminId(currentUser);

        if (resolvedId) {
          params.adminId = resolvedId;
        } else if (defaultAdminId) {
          params.adminId = defaultAdminId;
        }
        
        const response = await client.get("/api/admin/topbar/overview", { params });
        if (!isMounted) return;

        const payload = response.data ?? {};
        const nextProfile = payload.admin ?? {};
        const resolvedProfileId = resolveAdminId(nextProfile) ?? resolvedId ?? defaultAdminId ?? "default";
        const storedAvatar = readAvatarForAdmin(resolvedProfileId);
        setProfile((previous) => ({
          ...previous,
          ...nextProfile,
          fullName: nextProfile.fullName ?? previous.fullName,
          title: nextProfile.title ?? previous.title,
          initials: nextProfile.initials ?? previous.initials,
        }));
        setAvatarUrl(storedAvatar || null);

        setNotifications(normalizeNotificationsState(payload.notifications));

        setSecurity((previous) => ({
          ...previous,
          ...(payload.security ?? {}),
        }));

        setError(null);
      } catch (fetchError) {
        if (!isMounted) return;
        setError(fetchError?.response?.data?.message ?? "Unable to sync top bar data");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchOverview();
    fetchAppearance();
    const intervalId = setInterval(fetchOverview, 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [defaultAdminId]);

  const notificationTooltip = useMemo(() => {
    if (error) {
      return error;
    }
    if (!notifications.buckets.length) {
      return "No outstanding alerts";
    }
    const summary = notifications.buckets
      .filter((bucket) => Number(bucket.count) > 0)
      .map((bucket) => `${bucket.label}: ${bucket.count}`)
      .join(" • ");
    return summary || "No outstanding alerts";
  }, [error, notifications]);

  const badgeCount = Number(notifications.badgeCount ?? 0);
  const unreadNotificationCount = Number(notifications.unreadCount ?? 0);
  const badgeDisplay = loading ? "…" : badgeCount > 99 ? "99+" : badgeCount.toString();
  const secureLabel = error ? "Status sync issue" : security.message;
  const secureSuffix = !error && security.lastEventAgo ? ` • ${security.lastEventAgo}` : "";
  const secureTooltip = error
    ? error
    : `Active admin sessions: ${security.activeSessions ?? 0}${security.lastEventAgo ? ` | Last event ${security.lastEventAgo}` : ""}`;
  const trimmedSearch = searchTerm.trim();
  const hasMinSearchChars = trimmedSearch.length >= MIN_SEARCH_LENGTH;

  const unreadNotifications = useMemo(
    () => notifications.items.filter((item) => !item.isRead),
    [notifications.items],
  );

  const allNotificationsLimited = useMemo(
    () => notifications.items.slice(0, ALL_NOTIFICATIONS_LIMIT),
    [notifications.items, ALL_NOTIFICATIONS_LIMIT],
  );

  const filteredNotifications = notificationFilter === "unread"
    ? unreadNotifications
    : allNotificationsLimited;

  const notificationSections = useMemo(
    () => groupNotificationsByDate(filteredNotifications),
    [filteredNotifications],
  );

  useEffect(() => {
    if (!searchOpen) {
      return undefined;
    }
    const handleClick = (event) => {
      if (!searchContainerRef.current) return;
      if (!searchContainerRef.current.contains(event.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [searchOpen]);

  useEffect(() => {
    if (!notificationsOpen) {
      setNotificationsMenuOpen(false);
      return undefined;
    }
    const handleClick = (event) => {
      if (!notificationsRef.current) return;
      if (!notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
        setNotificationsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [notificationsOpen]);

  useEffect(() => {
    if (!searchOpen) {
      setSearchLoading(false);
      return undefined;
    }

    if (!hasMinSearchChars) {
      setSearchResults(createEmptySearchResults());
      setSearchError(null);
      setSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    setSearchLoading(true);
    setSearchError(null);
    const requestId = lastSearchRequestId.current + 1;
    lastSearchRequestId.current = requestId;

    const timeoutId = setTimeout(async () => {
      try {
        const response = await client.get("/api/admin/search/quick", { params: { q: searchTerm.trim() } });
        if (cancelled || requestId !== lastSearchRequestId.current) {
          return;
        }
        setSearchResults(normalizeSearchResults(response.data?.results));
        setSearchError(null);
      } catch (searchFetchError) {
        if (cancelled || requestId !== lastSearchRequestId.current) {
          return;
        }
        setSearchError(searchFetchError?.response?.data?.message ?? "Search failed");
      } finally {
        if (!cancelled && requestId === lastSearchRequestId.current) {
          setSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchTerm, searchOpen, hasMinSearchChars]);

  const hasSearchResults =
    searchResults.members.length
    || searchResults.tickets.length
    || searchResults.registrations.length
    || searchResults.events.length
    || searchResults.bookings.length
    || searchResults.venues.length
    || searchResults.idCards.length;

  const totalSearchResults =
    searchResults.members.length
    + searchResults.tickets.length
    + searchResults.registrations.length
    + searchResults.events.length
    + searchResults.bookings.length
    + searchResults.venues.length
    + searchResults.idCards.length;

  const closeSearchPanel = () => {
    setSearchOpen(false);
  };

  const currentAdminId = useMemo(() => {
    const fromProfile = resolveAdminId(profile) ?? parsePositiveNumber(profile?.adminId);
    if (fromProfile) {
      return fromProfile;
    }
    const storedUser = readStoredAdminUser();
    return resolveAdminId(storedUser) ?? parsePositiveNumber(defaultAdminId);
  }, [profile, defaultAdminId]);

  const refreshNotifications = useCallback(async () => {
    if (!currentAdminId) {
      setNotificationsError("Unable to resolve admin account.");
      return;
    }

    setNotificationsLoading(true);
    setNotificationsError(null);
    try {
      const response = await client.get("/api/admin/topbar/notifications", {
        params: {
          adminId: currentAdminId,
          limit: 120,
        },
      });
      setNotifications((previous) => {
        const nextFeed = normalizeNotificationsState(response.data);
        return {
          ...previous,
          ...nextFeed,
          badgeCount: Math.max(0, Number(nextFeed.unreadCount ?? 0)),
        };
      });
    } catch (notificationFetchError) {
      setNotificationsError(notificationFetchError?.response?.data?.message ?? "Unable to load notifications.");
    } finally {
      setNotificationsLoading(false);
    }
  }, [currentAdminId]);

  const handleNotificationClick = async (item) => {
    if (!item) {
      return;
    }

    const linkUrl = typeof item.linkUrl === "string" && item.linkUrl.trim().length
      ? item.linkUrl
      : "/admin/dashboard";

    if (!item.isRead && currentAdminId) {
      setNotifications((previous) => {
        const nextItems = previous.items.map((entry) => (
          entry.key === item.key ? { ...entry, isRead: true } : entry
        ));
        return {
          ...previous,
          items: nextItems,
          unreadCount: Math.max(0, Number(previous.unreadCount ?? 0) - 1),
          badgeCount: Math.max(0, Number(previous.badgeCount ?? 0) - 1),
        };
      });

      try {
        await client.patch(`/api/admin/topbar/notifications/${encodeURIComponent(item.key)}/read`, {
          adminId: currentAdminId,
        });
      } catch (markReadError) {
        console.error("Unable to mark notification as read", markReadError);
      }
    }

    setNotificationsOpen(false);
    navigate(linkUrl);
  };

  const handleMarkAllNotificationsRead = async () => {
    if (!currentAdminId) {
      return;
    }

    const unreadKeys = notifications.items
      .filter((item) => !item.isRead)
      .map((item) => item.key)
      .filter(Boolean);

    if (!unreadKeys.length) {
      return;
    }

    setNotifications((previous) => {
      const unreadTotal = previous.items.filter((item) => !item.isRead).length;
      return {
        ...previous,
        items: previous.items.map((item) => ({ ...item, isRead: true })),
        unreadCount: 0,
        badgeCount: Math.max(0, Number(previous.badgeCount ?? 0) - unreadTotal),
      };
    });

    try {
      await client.patch("/api/admin/topbar/notifications/read-all", {
        adminId: currentAdminId,
        keys: unreadKeys,
      });
    } catch (markAllError) {
      console.error("Unable to mark all notifications as read", markAllError);
      refreshNotifications();
    }
  };

  const handleSeeAllNotifications = () => {
    setNotificationFilter("all");
  };

  const handleNotificationMenuToggle = (event) => {
    event.stopPropagation();
    setNotificationsMenuOpen((open) => !open);
  };

  const handleOpenNotificationsMenuAction = () => {
    setNotificationFilter("all");
    setNotificationsMenuOpen(false);
    setNotificationsOpen(false);
    navigate("/admin/notifications");
  };

  useEffect(() => {
    if (!notificationsOpen) {
      return;
    }
    refreshNotifications();
  }, [notificationsOpen, refreshNotifications]);

  const handleSelectMember = (member) => {
    navigate(`/admin/members/${member.id}`);
    closeSearchPanel();
  };

  const handleSelectTicket = (ticket) => {
    navigate(`/admin/ticket-detail?id=${ticket.id}`);
    closeSearchPanel();
  };

  const handleSelectRegistration = () => {
    navigate("/admin/registration-review");
    closeSearchPanel();
  };

  const handleSelectEvent = () => {
    navigate("/admin/event-management");
    closeSearchPanel();
  };

  const handleSelectBooking = () => {
    navigate("/admin/client-bookings");
    closeSearchPanel();
  };

  const handleSelectVenue = () => {
    navigate("/admin/venue-management");
    closeSearchPanel();
  };

  const handleSelectIdCard = () => {
    navigate("/admin/id-card-management");
    closeSearchPanel();
  };

  const handleSearchClear = () => {
    setSearchTerm("");
    setSearchError(null);
    setSearchResults(createEmptySearchResults());
    closeSearchPanel();
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      closeSearchPanel();
      return;
    }

    if (event.key !== "Enter") {
      return;
    }

    if (!hasMinSearchChars || !totalSearchResults) {
      return;
    }

    event.preventDefault();

    const [firstMember] = searchResults.members;
    const [firstTicket] = searchResults.tickets;
    const [firstRegistration] = searchResults.registrations;
    const [firstEvent] = searchResults.events;
    const [firstBooking] = searchResults.bookings;
    const [firstVenue] = searchResults.venues;
    const [firstIdCard] = searchResults.idCards;

    if (firstMember) {
      handleSelectMember(firstMember);
      return;
    }

    if (firstTicket) {
      handleSelectTicket(firstTicket);
      return;
    }

    if (firstRegistration) {
      handleSelectRegistration(firstRegistration);
      return;
    }

    if (firstEvent) {
      handleSelectEvent(firstEvent);
      return;
    }

    if (firstBooking) {
      handleSelectBooking(firstBooking);
      return;
    }

    if (firstVenue) {
      handleSelectVenue(firstVenue);
      return;
    }

    if (firstIdCard) {
      handleSelectIdCard(firstIdCard);
    }
  };

  const renderSearchSection = (title, items, renderItem) => {
    if (!items.length) {
      return null;
    }
    return (
      <div className="admin-search-panel__section" key={title}>
        <div className="admin-search-panel__section-label">{title}</div>
        <ul>
          {items.map((item) => (
            <li key={item.id || item.ticketNo}>{renderItem(item)}</li>
          ))}
        </ul>
      </div>
    );
  };

  const renderMember = (member) => (
    <button
      type="button"
      className="admin-search-panel__item"
      onClick={() => handleSelectMember(member)}
    >
      <strong>{member.fullName}</strong>
      <span>{member.email}</span>
      <span className="admin-search-panel__meta">{member.membershipNo || member.status}</span>
    </button>
  );

  const renderTicket = (ticket) => (
    <button
      type="button"
      className="admin-search-panel__item"
      onClick={() => handleSelectTicket(ticket)}
    >
      <strong>{ticket.ticketNo}</strong>
      <span>{ticket.subject}</span>
      <span className="admin-search-panel__meta">{ticket.status}</span>
    </button>
  );

  const renderRegistration = (entry) => (
    <button
      type="button"
      className="admin-search-panel__item"
      onClick={() => handleSelectRegistration(entry)}
    >
      <strong>{entry.fullName}</strong>
      <span>{entry.email}</span>
      <span className="admin-search-panel__meta">Submitted {entry.submittedAgo || 'recently'}</span>
    </button>
  );

  const renderEvent = (event) => (
    <button
      type="button"
      className="admin-search-panel__item"
      onClick={() => handleSelectEvent(event)}
    >
      <strong>{event.title}</strong>
      <span>{event.venue || event.category || "Event"}</span>
      <span className="admin-search-panel__meta">{event.startAgo ? `Starts ${event.startAgo}` : "Event Management"}</span>
    </button>
  );

  const renderBooking = (booking) => (
    <button
      type="button"
      className="admin-search-panel__item"
      onClick={() => handleSelectBooking(booking)}
    >
      <strong>{booking.customerName}</strong>
      <span>{booking.venueName || "Venue booking"}</span>
      <span className="admin-search-panel__meta">{booking.status || "Client booking"}</span>
    </button>
  );

  const renderVenue = (venue) => (
    <button
      type="button"
      className="admin-search-panel__item"
      onClick={() => handleSelectVenue(venue)}
    >
      <strong>{venue.name}</strong>
      <span>{venue.location || venue.type || "Venue"}</span>
      <span className="admin-search-panel__meta">{venue.isVisible ? "Visible" : "Hidden"}</span>
    </button>
  );

  const renderIdCard = (item) => (
    <button
      type="button"
      className="admin-search-panel__item"
      onClick={() => handleSelectIdCard(item)}
    >
      <strong>{item.requestNo || `ID-${item.id}`}</strong>
      <span>{item.fullName || item.email || "Member"}</span>
      <span className="admin-search-panel__meta">{item.status || item.cardType || "ID card request"}</span>
    </button>
  );

  const displayName = profile.fullName || buildAdminDisplayName(profile) || defaultAdminName;
  const displayRole = profile.title || (Array.isArray(profile.roles) && profile.roles[0]) || "Admin User";
  const hasBasicInfoChanges = useMemo(() => {
    const trimmedFirst = accountForm.firstName.trim();
    const trimmedLast = accountForm.lastName.trim();
    return trimmedFirst !== (profile.firstName ?? "") || trimmedLast !== (profile.lastName ?? "");
  }, [accountForm.firstName, accountForm.lastName, profile.firstName, profile.lastName]);
  const pageMeta = useMemo(() => {
    const parts = location.pathname.replace(/\/+$/g, "").split("/");
    const rawSlug = parts[2] || "dashboard";
    const normalizedSlug = rawSlug === "ticket-detail" ? "tickets" : rawSlug;
    const fallbackTitle = normalizedSlug
      .split("-")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ") || "Admin Console";

    return TOPBAR_PAGE_META[normalizedSlug] || {
      title: fallbackTitle,
      subtitle: "Manage workflows and monitor your admin workspace.",
    };
  }, [location.pathname]);

  const handleLogout = async () => {
    const storedUser = readStoredAdminUser();
    const actorId = resolveAdminId(storedUser);
    const actorName = buildAdminDisplayName(storedUser) ?? defaultAdminName;

    try {
      await client.post("/api/admin/audit/logs", {
        action: "admin_logout",
        entityType: "auth",
        entityId: actorId ?? null,
        actorAdminId: actorId ?? null,
        metadata: {
          actorName,
          actorType: "admin",
          status: "logged_out",
        },
      });
    } catch (auditError) {
      console.error("Unable to record logout audit entry", auditError);
    } finally {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUser");
      delete client.defaults.headers.common.Authorization;
      navigate("/admin/login");
    }
  };

  useEffect(() => {
    if (!appearanceStatus || appearanceStatus.type !== "success") {
      return undefined;
    }
    const timeoutId = setTimeout(() => setAppearanceStatus(null), 2000);
    return () => clearTimeout(timeoutId);
  }, [appearanceStatus]);

  const persistAppearancePreferences = async (prefs) => {
    setAppearanceSaving(true);
    setAppearanceStatus(null);
    try {
      await client.put("/api/admin/settings/system", { settings: { appearancePreferences: prefs } });
      setAppearanceStatus({ type: "success", text: "Appearance updated." });
    } catch (err) {
      setAppearanceStatus({ type: "error", text: err?.response?.data?.message ?? "Unable to save appearance." });
    } finally {
      setAppearanceSaving(false);
    }
  };

  const handleAppearancePreferenceChange = (updates) => {
    setAppearancePrefs((prev) => {
      const next = normalizeAppearancePreferences({ ...prev, ...updates });
      if (next.theme === prev.theme && next.fontScale === prev.fontScale) {
        return prev;
      }
      persistAppearancePreferences(next);
      return next;
    });
  };

  const openSettings = () => {
    setProfileOpen(false);
    setAppearanceStatus(null);
    setSettingsModalOpen(true);
  };

  const handleAccountChange = (field, value) => {
    setAccountErrors((prev) => ({ ...prev, [field]: null }));
    setAccountForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAccountSave = async () => {
    setAccountStatus(null);
    setAccountErrors({});

    const trimmedFirst = accountForm.firstName.trim();
    const trimmedLast = accountForm.lastName.trim();

    const nameChanged = trimmedFirst !== (profile.firstName ?? "") || trimmedLast !== (profile.lastName ?? "");

    if (!nameChanged) {
      setAccountStatus({ type: "info", text: "No changes to save." });
      return;
    }

    const payload = {};
    if (nameChanged) {
      if (trimmedFirst) {
        payload.firstName = trimmedFirst;
      }
      if (trimmedLast) {
        payload.lastName = trimmedLast;
      }
    }

    setAccountSaving(true);
    try {
      const response = await client.put("/api/admin/auth/profile", payload);

      if (response?.data?.success === false || response?.data?.error || response?.data?.errorMessage) {
        const message = response?.data?.error || response?.data?.errorMessage || response?.data?.message || "Unable to update profile.";
        setAccountStatus({ type: "error", text: message });
        return;
      }

      const stored = readStoredAdminUser() || {};
      const nextStored = { ...stored };
      if (payload.firstName) {
        nextStored.firstName = payload.firstName;
        if (nextStored.admin) nextStored.admin.firstName = payload.firstName;
      }
      if (payload.lastName) {
        nextStored.lastName = payload.lastName;
        if (nextStored.admin) nextStored.admin.lastName = payload.lastName;
      }
      if (Object.keys(nextStored).length) {
        window.localStorage.setItem("adminUser", JSON.stringify(nextStored));
      }

      setProfile((prev) => {
        const updated = {
          ...prev,
          firstName: payload.firstName ?? prev.firstName,
          lastName: payload.lastName ?? prev.lastName,
        };
        const mergedName = [updated.firstName, updated.lastName].filter(Boolean).join(" ").trim();
        if (mergedName) {
          updated.fullName = mergedName;
          updated.initials = computeInitials(mergedName);
        }
        return updated;
      });

      setAccountForm((prev) => ({ ...prev }));
      setAccountStatus({ type: "success", text: "Profile updated." });
    } catch (accountError) {
      const serverMessage = accountError?.response?.data?.message;
      const message = serverMessage ?? "Unable to update profile.";
      setAccountStatus({ type: "error", text: message });
    } finally {
      setAccountSaving(false);
    }
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const imageSource = await fileToDataUrl(file);
      setAvatarCropSource(imageSource);
      setAvatarCrop({ x: 0, y: 0 });
      setAvatarZoom(1);
      setAvatarCroppedAreaPixels(null);
    } catch {
      // silently ignore unreadable files
    }
    event.target.value = "";
  };

  const handleAvatarCropComplete = (_, croppedAreaPixels) => {
    setAvatarCroppedAreaPixels(croppedAreaPixels);
  };

  const applyAvatarCrop = async () => {
    if (!avatarCropSource || !avatarCroppedAreaPixels) return;
    try {
      setIsApplyingCrop(true);
      const croppedDataUrl = await getCroppedImageDataUrl(
        avatarCropSource,
        avatarCroppedAreaPixels,
        512,
        "image/jpeg",
        0.92
      );
      setAvatarPreview(croppedDataUrl);
      const currentUser = readStoredAdminUser();
      const resolvedId = resolveAdminId(currentUser) ?? defaultAdminId ?? "default";
      persistAvatarForAdmin(resolvedId, croppedDataUrl);
      setAvatarUrl(croppedDataUrl);
      setAvatarCropSource(null);
    } catch {
      // crop failed, stay in crop mode
    } finally {
      setIsApplyingCrop(false);
    }
  };

  return (
    <>
    <header className="admin-topbar">
      <div
        className={`admin-topbar__search ${searchOpen ? "is-open" : "is-collapsed"}`}
        ref={searchContainerRef}
      >
        <Search size={16} />
        <input
          type="search"
          placeholder="Search tickets, members, files"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          onFocus={() => setSearchOpen(true)}
          onKeyDown={handleSearchKeyDown}
          aria-label="Quick search"
        />
        {searchTerm.trim().length ? (
          <button
            type="button"
            className="admin-topbar__search-clear"
            onClick={handleSearchClear}
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        ) : null}
        {searchOpen ? (
          <div className="admin-search-panel">
            {hasMinSearchChars && !searchLoading && !searchError && hasSearchResults ? (
              <div className="admin-search-panel__summary">{totalSearchResults} result{totalSearchResults === 1 ? "" : "s"}</div>
            ) : null}
            {!hasMinSearchChars ? (
              <div className="admin-search-panel__status">Type at least {MIN_SEARCH_LENGTH} characters to search.</div>
            ) : null}
            {hasMinSearchChars && searchLoading ? (
              <div className="admin-search-panel__status">Searching…</div>
            ) : null}
            {hasMinSearchChars && searchError ? (
              <div className="admin-search-panel__status is-error">{searchError}</div>
            ) : null}
            {hasMinSearchChars && !searchLoading && !searchError && !hasSearchResults ? (
              <div className="admin-search-panel__status">No results found for your role</div>
            ) : null}
            {renderSearchSection("Members", searchResults.members, renderMember)}
            {renderSearchSection("Tickets", searchResults.tickets, renderTicket)}
            {renderSearchSection("Registrations", searchResults.registrations, renderRegistration)}
            {renderSearchSection("Event Management", searchResults.events, renderEvent)}
            {renderSearchSection("Client Bookings", searchResults.bookings, renderBooking)}
            {renderSearchSection("Venue Management", searchResults.venues, renderVenue)}
            {renderSearchSection("ID Card Requests", searchResults.idCards, renderIdCard)}
          </div>
        ) : null}
      </div>
      <div className="admin-topbar__right">
        {/* <span className="admin-topbar__shortcut" title={secureTooltip}>  
          <ShieldCheck size={16} />
          {" "}
          {secureLabel}
          {secureSuffix}
        </span>
        <button type="button" className="admin-topbar__icon" aria-label="AI suggestions">
          <Sparkles size={18} />
        </button> */}
        <div className="admin-topbar__icon-group" ref={notificationsRef}>
          <button
            className="admin-topbar__icon"
            type="button"
            aria-haspopup="dialog"
            aria-expanded={notificationsOpen}
            onClick={() => setNotificationsOpen((open) => !open)}
            title={notificationTooltip}
          >
            <BellRing size={18} />
            {badgeCount > 0 ? (
              <span className="admin-badge is-critical">{badgeDisplay}</span>
            ) : null}
          </button>

          {notificationsOpen ? (
            <div className="admin-notification-popover" role="dialog">
              <div className="admin-notification-popover__header">
                <div className="admin-notification-popover__header-row">
                  <h4>Notifications</h4>
                  <button
                    type="button"
                    className="admin-notification-popover__more"
                    aria-label="Notification options"
                    aria-haspopup="menu"
                    aria-expanded={notificationsMenuOpen}
                    onClick={handleNotificationMenuToggle}
                  >
                    •••
                  </button>
                  {notificationsMenuOpen ? (
                    <div className="admin-notification-menu" role="menu" aria-label="Notification actions">
                      <button
                        type="button"
                        role="menuitem"
                        className="admin-notification-menu__item"
                        onClick={() => {
                          setNotificationsMenuOpen(false);
                          handleMarkAllNotificationsRead();
                        }}
                        disabled={!unreadNotificationCount || notificationsLoading}
                      >
                        <Check size={16} />
                        <span>Mark all as read</span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="admin-notification-menu__item"
                        onClick={handleOpenNotificationsMenuAction}
                      >
                        <Monitor size={16} />
                        <span>Open Notifications</span>
                      </button>
                    </div>
                  ) : null}
                </div>
                <div className="admin-notification-popover__tabs">
                  <button
                    type="button"
                    className={`admin-notification-popover__tab ${notificationFilter === "all" ? "is-active" : ""}`.trim()}
                    onClick={() => setNotificationFilter("all")}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`admin-notification-popover__tab ${notificationFilter === "unread" ? "is-active" : ""}`.trim()}
                    onClick={() => setNotificationFilter("unread")}
                  >
                    Unread
                  </button>
                </div>
              </div>
              <div className="admin-notification-popover__actions">
                <span className="admin-notification-popover__count">
                  {notificationFilter === "unread"
                    ? `${filteredNotifications.length} unread update${filteredNotifications.length === 1 ? "" : "s"}`
                    : `Showing ${filteredNotifications.length} of ${notifications.items.length} notifications`}
                </span>
              </div>
              {notificationsLoading ? (
                <div className="admin-notification-popover__status">Loading notifications…</div>
              ) : null}
              {!notificationsLoading && notificationsError ? (
                <div className="admin-notification-popover__status is-error">{notificationsError}</div>
              ) : null}
              {!notificationsLoading && !notificationsError && !filteredNotifications.length ? (
                <div className="admin-notification-popover__status">No notifications available</div>
              ) : null}
              {!notificationsLoading && !notificationsError && filteredNotifications.length ? (
                <div className="admin-notification-sections">
                  {notificationSections.map((section, index) => (
                    <section className="admin-notification-section" key={section.key}>
                      <div className="admin-notification-section__header">
                        <h5>{section.label}</h5>
                        {index === 0 && notificationFilter === "unread" ? (
                          <button
                            type="button"
                            className="admin-notification-popover__seeall"
                            onClick={handleSeeAllNotifications}
                          >
                            See all
                          </button>
                        ) : null}
                      </div>
                      <ul className="admin-notification-list">
                        {section.items.map((item) => (
                          <li key={item.key}>
                            <button
                              type="button"
                              className={`admin-notification-item is-${item.priority || "info"} ${item.isRead ? "is-read" : ""}`.trim()}
                              onClick={() => handleNotificationClick(item)}
                            >
                              <span className={`admin-notification-item__avatar is-${item.priority || "info"}`.trim()}>
                                {resolveNotificationInitial(item)}
                              </span>
                              <span className="admin-notification-item__content">
                                <span className="admin-notification-item__title">{item.title}</span>
                                <span className="admin-notification-item__message">{item.message}</span>
                                <span className="admin-notification-item__meta">
                                  <span className="admin-notification-item__pill">{item.priority || "info"}</span>
                                  <span>{item.timeAgo || "just now"}</span>
                                </span>
                              </span>
                              {!item.isRead ? <span className="admin-notification-item__dot" aria-hidden="true" /> : null}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div 
          className="admin-topbar__profile"
          onClick={() => setProfileOpen(!profileOpen)}
          ref={profileRef}
        >
          <div
            className="admin-avatar admin-avatar--sm"
            style={avatarUrl ? { backgroundImage: `url(${avatarUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
          >
            {!avatarUrl ? <UserCircle size={28} /> : null}
          </div>
          <div className="admin-topbar__profile-info">
            <strong>{displayName}</strong>
            <span>{displayRole}</span>
          </div>
          <span className="admin-topbar__profile-caret" aria-hidden="true">▾</span>
          {profileOpen && (
            <div className="admin-profile-dropdown">
              <div className="admin-profile-dropdown__header">
                <strong>{displayName}</strong>
                <span>{displayRole}</span>
              </div>
              <button className="admin-profile-dropdown__item" onClick={openSettings}>
                <UserPen size={16} />
                Edit Profile
              </button>
              <div className="admin-profile-dropdown__divider" />
              <button 
                className="admin-profile-dropdown__item is-danger" 
                onClick={(e) => {
                  e.stopPropagation();
                  setLogoutConfirmOpen(true);
                  setProfileOpen(false);
                }}
              >
                <LogOut size={16} />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
      {settingsModalOpen && (
        <div className="admin-settings-modal">
          <div className="admin-settings-modal__content">
            {/* Header */}
            <div className="admin-settings-modal__header">
              <div>
                <h3>Edit Profile</h3>
                <p className="admin-settings-modal__subtitle">Update your account details and interface preferences.</p>
              </div>
              <button
                className="admin-settings-modal__close"
                onClick={() => setSettingsModalOpen(false)}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="admin-settings-modal__body">

              {/* Profile Hero Banner */}
              <div className="admin-profile-edit__banner">
                {avatarCropSource ? (
                  <>
                    <div className="admin-profile-edit__crop-stage">
                      <Cropper
                        image={avatarCropSource}
                        crop={avatarCrop}
                        zoom={avatarZoom}
                        aspect={1}
                        cropShape="round"
                        showGrid={false}
                        onCropChange={setAvatarCrop}
                        onZoomChange={setAvatarZoom}
                        onCropComplete={handleAvatarCropComplete}
                      />
                    </div>
                    <div className="admin-profile-edit__crop-controls">
                      <label className="admin-profile-edit__zoom-label">
                        <span>Zoom</span>
                        <input
                          type="range"
                          min={1}
                          max={3}
                          step={0.05}
                          value={avatarZoom}
                          onChange={(e) => setAvatarZoom(Number(e.target.value))}
                        />
                      </label>
                      <div className="admin-profile-edit__crop-actions">
                        <button
                          type="button"
                          className="admin-button is-primary"
                          onClick={applyAvatarCrop}
                          disabled={isApplyingCrop}
                        >
                          {isApplyingCrop ? "Applying..." : "Apply Crop"}
                        </button>
                        <button
                          type="button"
                          className="admin-button admin-button--ghost admin-button--ghost-inverse"
                          onClick={() => setAvatarCropSource(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <label className="admin-profile-edit__avatar-wrap">
                      <div
                        className="admin-profile-edit__avatar"
                        style={
                          avatarPreview
                            ? { backgroundImage: `url(${avatarPreview})`, backgroundSize: "cover", backgroundPosition: "center" }
                            : {}
                        }
                      >
                        {!avatarPreview && <UserCircle size={52} color="#7f5af0" />}
                      </div>
                      <div className="admin-profile-edit__camera-overlay">
                        <Camera size={20} />
                      </div>
                      <input type="file" accept="image/*" className="admin-sr-only" onChange={handleAvatarChange} />
                    </label>
                    <div className="admin-profile-edit__identity">
                      <div className="admin-profile-edit__name">{displayName}</div>
                      <div className="admin-profile-edit__role">{displayRole}</div>
                    </div>
                  </>
                )}
              </div>

              <div className="admin-form-section admin-form-section--first admin-profile-edit__section">
                <h4 className="admin-profile-edit__section-title">Basic Information</h4>
                <div className="admin-field">
                  <span>First Name</span>
                  <input
                    value={accountForm.firstName}
                    onChange={(event) => handleAccountChange("firstName", event.target.value)}
                    placeholder="First name"
                    disabled={accountSaving}
                  />
                  {accountErrors.firstName ? (
                    <small className="admin-field__error">{accountErrors.firstName}</small>
                  ) : null}
                </div>
                <div className="admin-field">
                  <span>Last Name</span>
                  <input
                    value={accountForm.lastName}
                    onChange={(event) => handleAccountChange("lastName", event.target.value)}
                    placeholder="Last name"
                    disabled={accountSaving}
                  />
                  {accountErrors.lastName ? (
                    <small className="admin-field__error">{accountErrors.lastName}</small>
                  ) : null}
                </div>
                <div className="admin-profile-edit__actions">
                  <button
                    type="button"
                    className="admin-button is-primary"
                    onClick={handleAccountSave}
                    disabled={accountSaving || !hasBasicInfoChanges}
                  >
                    {accountSaving ? "Applying..." : "Apply Changes"}
                  </button>
                </div>
                {accountStatus ? (
                  <div className={`admin-settings-modal__status admin-settings-modal__status--${accountStatus.type}`}>
                    {accountStatus.text}
                  </div>
                ) : null}
              </div>

              <div className="admin-form-section admin-profile-edit__section">
                <h4 className="admin-profile-edit__section-title">Appearance Preferences</h4>
                <div>
                  <p className="admin-muted">Personalize the admin console theme and base font size.</p>
                </div>
                <div className="admin-field">
                  <span>Theme Mode</span>
                  <div className="admin-row admin-row--start">
                    <button
                      type="button"
                      className={`admin-button ${appearancePrefs.theme === "light" ? "is-primary" : ""}`.trim()}
                      onClick={() => handleAppearancePreferenceChange({ theme: "light" })}
                      aria-pressed={appearancePrefs.theme === "light"}
                    >
                      Bright Mode
                    </button>
                    <button
                      type="button"
                      className={`admin-button ${appearancePrefs.theme === "dark" ? "is-primary" : ""}`.trim()}
                      onClick={() => handleAppearancePreferenceChange({ theme: "dark" })}
                      aria-pressed={appearancePrefs.theme === "dark"}
                    >
                      Dark Mode
                    </button>
                  </div>
                </div>
                <div className="admin-field">
                  <span>Font Size</span>
                  <div className="admin-row admin-row--start">
                    <button
                      type="button"
                      className={`admin-button ${appearancePrefs.fontScale === "small" ? "is-primary" : ""}`.trim()}
                      onClick={() => handleAppearancePreferenceChange({ fontScale: "small" })}
                      aria-pressed={appearancePrefs.fontScale === "small"}
                    >
                      Smaller
                    </button>
                    <button
                      type="button"
                      className={`admin-button ${appearancePrefs.fontScale === "medium" ? "is-primary" : ""}`.trim()}
                      onClick={() => handleAppearancePreferenceChange({ fontScale: "medium" })}
                      aria-pressed={appearancePrefs.fontScale === "medium"}
                    >
                      Default
                    </button>
                    <button
                      type="button"
                      className={`admin-button ${appearancePrefs.fontScale === "large" ? "is-primary" : ""}`.trim()}
                      onClick={() => handleAppearancePreferenceChange({ fontScale: "large" })}
                      aria-pressed={appearancePrefs.fontScale === "large"}
                    >
                      Larger
                    </button>
                  </div>
                  <small className="admin-muted">Updates apply instantly and save automatically.</small>
                </div>
                {appearanceSaving ? (
                  <div className="admin-settings-modal__status admin-settings-modal__status--info">Saving appearance...</div>
                ) : null}
                {appearanceStatus ? (
                  <div className={`admin-settings-modal__status admin-settings-modal__status--${appearanceStatus.type}`}>
                    {appearanceStatus.text}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Footer */}
            <div className="admin-settings-modal__footer">
              <button
                type="button"
                className="admin-settings-modal__save"
                onClick={() => setSettingsModalOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {logoutConfirmOpen ? (
        <div className="admin-dialog" role="dialog" aria-modal="true" aria-label="Confirm admin logout">
          <div className="admin-dialog__backdrop" onClick={() => setLogoutConfirmOpen(false)} />
          <div className="admin-dialog__panel" style={{ maxWidth: 460 }}>
            <div className="admin-dialog__header">
              <h3>Confirm Logout</h3>
            </div>
            <div className="admin-dialog__body">
              <p className="admin-muted">Are you sure you want to sign out of the admin console?</p>
            </div>
            <div className="admin-dialog__footer">
              <button
                type="button"
                className="admin-button"
                onClick={() => setLogoutConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-button is-danger"
                onClick={() => {
                  setLogoutConfirmOpen(false);
                  handleLogout();
                }}
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

TopBar.propTypes = {
  onToggleSidebar: PropTypes.func,
};
